import { gql } from '@apollo/client';

export const typeDefs = gql`
    type User {
        id: ID!
        userSub: ID!
        email: String!
        name: String!
        statementId: String!
        username: String!
    }

    type CodeDeliveryDetails {
        attributeName: String
        deliveryMedium: String
        destination: String
    }

    type Session {
        token: String
        codeDeliveryDetails: CodeDeliveryDetails
        currentUser: User
        expiration: Int
        isSigningUp: Boolean
        issuedAt: Int
        sessionIsValid: Boolean
    }

    type UIState {
        currentlyPlayingAudio: ID # The HTML id of an audio element that is currently playing, if
        # any. There will only ever be one audio element playing at a time.
        detections: Detections
        activeLogPolls: ActiveLogPolls
        statements: Statements
        tracks: Tracks
    }

    # When any values in this object are true, log polling will use the short interval value.
    # Otherwise, log polling will use the long interval value.
    type ActiveLogPolls {
        analyzeTrqkGap: Boolean
        analyzeTrqkGapChannel: Boolean
        exportGapAnalysis: Boolean
        uploadDetections: Boolean
        uploadStatements: Boolean
    }

    type Detections {
        showChart: Boolean
    }

    type Statements {
        showUploadHasStarted: Boolean
        statementId: String
        unviewedStatements: [String]
    }

    enum StatementsDisplayMode {
        charts
        table
    }

    type Tracks {
        viewUnmatchedTracksOnly: Boolean
    }

    type LogEvent {
        key: ID!
        date: String!
        eventStatus: String!
        eventType: String!
        isValid: Boolean!
        userId: String!
    }

    type Query {
        proIngestion: PROIngestion
        proIngestions: [PROIngestion]
        session: Session
        uiState: UIState
        users: [User]
    }

    scalar Upload

    scalar ResolverError

    type S3UploadResult {
        filename: String
        key: ID
        location: String
    }

    type S3ObjectIdentifier {
        Key: String!
        VersionId: String
    }

    type PROIngestion {
        id: ID!
        data: PROIngestionData
        filename: String!
        statementId: ID!
    }

    type PROIngestionData {
        dashboard: PROIngestionDashboard
        status_code: Int
    }

    type PROIngestionDashboard {
        chart_tracks: PROChartTrackWrapper
        country_codes: [PROCountryCode]
        kpis: PROKpis
        music_user_genres: [PROMusicUserGenre]
        directed_tree: PRODirectedTreeNode
        shows: [PROShow]
        sources: [PROSource]
        statement_info: PROStatementInfo
        tracks: [PROTrack]
        use_codes: [PROUseCode]
    }

    type PROChartTrackWrapper {
        digital: [PROChartTrack]
        nondigital: [PROChartTrack]
    }

    type PROChartTrack {
        digital_earnings: Float!
        earnings: Float!
        music_user_genre_name_contributions: [PROMusicUserGenreNameContribution!]!
        nondigital_earnings: Float!
        percent_share: Float
        work_title: String!
    }

    type PROCountryCode {
        country_code: String
        country_name: String
        earnings: Float
    }

    type PROKpis {
        earnings: Float
        performances: Int
        sources: Int
        tracks_this_statement: Int
    }

    type PROMusicUserGenre {
        earnings: Float
        music_user_genre_name: PROMusicUserGenreName
        plays: Int
    }

    enum PROMusicUserGenreName {
        broadcast
        digital
        live
        miscellaneous
        not_reported
        radio
    }

    type PRODirectedTreeNode {
        children: [PRODirectedTreeNode]
        earnings: Float # only tracks have earnings; in other entity_types this is null
        entity_name: String
        entity_type: PRODirectedTreeNodeEntity
        plays: Int # only tracks have plays; in other entity_types this is null
    }

    enum PRODirectedTreeNodeEntity {
        composer
        music_user_genre
        publisher
        subpublisher
        track
        unknown
    }

    type PROShow {
        earnings: Float
        plays: Int
        show_name: String
    }

    type PROSource {
        max_earnings_per_play: Float
        min_earnings_per_play: Float
        plays: Int
        source_name: String
    }

    type PROStatementInfo {
        role_type: PRORoleType
        name: String
        normed_statement_url: String
        period: PROStatementPeriod
        pro: String
        statement_id: ID!
        user_id: ID!
    }

    enum PRORoleType {
        admin
        publisher
        unknown
        writer
    }

    type PROStatementPeriod {
        end_date: String
        start_date: String
        unit: String
    }

    type PROTrack {
        composers: [String]
        earnings: Float
        max_earnings_per_play: Float
        min_earnings_per_play: Float
        music_user_genre_name_contributions: [PROMusicUserGenreNameContribution]
        percent_share: Float
        performance_contributions: [PROPerformanceContribution]
        plays: Int
        publishers: [String]
        show_name_contributions: [PROShowNameContribution]
        source_name_contributions: [PROSourceNameContribution]
        subpublishers: [String]
        use_name_contributions: [PROUseNameContribution]
        work_ids: [String]
        work_title: String
    }

    type PROMusicUserGenreNameContribution {
        earnings_contribution: Float
        music_user_genre_name: PROMusicUserGenreName
        plays_contribution: Int
    }

    type PROPerformanceContribution {
        duration: String
        earnings: Float
        plays: Int
        use_name: String
        show_name: String
        source_name: String
    }

    type PROShowNameContribution {
        earnings_contribution: Float
        plays_contribution: Int
        show_name: String
    }

    type PROSourceNameContribution {
        earnings_contribution: Float
        plays_contribution: Int
        source_name: String
    }

    type PROUseNameContribution {
        earnings_contribution: Float
        plays_contribution: Int
        use_name: String
    }

    type PROUseCode {
        earnings: Float
        plays: Int
        use_code: String
    }

    type TrqkStatementView {
        id: ID!
        earnings: Float
        end_date: String
        name: String
        normed_statement_url: String
        performances: Int
        pro: String
        region: PRORegionType
        role_type: PRORoleType
        sources: Int
        start_date: String
        statement_id: String
        status_name: TrqkStatementViewStatusName
        status_message: String
        tracks_this_statement: Int
        unit: String
        user_id: String
        version: Float!
        view: PROIngestionDashboard
    }

    enum TrqkStatementViewStatusName {
        PENDING
        BUILDING
        ANALYZING
        RENDERING
        COMPLETE
        ERROR
        FAILURE
    }

    enum PRORegionType {
        standard
        international
    }

    type ManageDetectionsUpdate {
        statusChange: ManageDetectionsStatusChange!
        id: String!
    }

    type ManageDetectionsStatusChange {
        draft: Boolean
        timestamp: String!
        status: DetectionStatus!
        source: DetectionStatusChangeSource!
    }

    type StatementPerformanceDataInput {
        statementId: String!
        workTitle: String
    }

    enum DetectionStatusChangeSource {
        ADMIN
        CLIENT
        TUNESAT
        VIDVITA
    }

    type DoTaskUpdates {
        error: Boolean
        progress: Boolean
        success: Boolean
    }

    type Mutation {
        confirmRegistrationWithCognito(
            username: String!
            verificationCode: String!
            clientId: String!
            userPoolId: String!
        ): User!
        ingestProStatement(
            statementUrl: String!
            proIngestionUrl: String!
            filename: String!
        ): PROIngestion
        signInWithCognito(
            username: String!
            password: String!
            clientId: String!
            userPoolId: String!
            updateUiState: Boolean!
        ): String!
        signUpWithCognito(
            username: String!
            password: String!
            clientId: String!
            userPoolId: String!
        ): SignUpResult!
        uploadToS3(
            bucketName: String!
            bucketRegion: String!
            identityPoolId: String!
            files: [Upload]!
        ): [S3UploadResult]
    }
`;
